import React, { useState, useRef, useEffect } from 'react';
import { Search, Grid, List, Plus, ChevronDown, X } from 'lucide-react';
import { isMobile } from 'react-device-detect';

const CompanySearchFilter = ({
  searchTerm = '',
  setSearchTerm = () => {},
  filterIndustry = 'all',
  setFilterIndustry = () => {},
  filterCity = 'all',
  setFilterCity = () => {},
  industries = [],
  cities = [],
  viewMode = 'list',
  setViewMode = () => {},
  navigateToAddCompany = () => {},
  isLoading = false,
  handleClearFilters
}) => {
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [citySearchTerm, setCitySearchTerm] = useState('');
  const cityDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setCityDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter cities based on search term - with null safety
  const filteredCities = React.useMemo(() => {
    if (!Array.isArray(cities)) {
      console.warn('CompanySearchFilter: cities prop should be an array, received:', typeof cities);
      return [];
    }
    
    if (!citySearchTerm || typeof citySearchTerm !== 'string') {
      return cities;
    }

    return cities.filter(city => {
      if (typeof city !== 'string') {
        console.warn('CompanySearchFilter: city item should be a string, received:', typeof city);
        return false;
      }
      return city.toLowerCase().includes(citySearchTerm.toLowerCase());
    });
  }, [cities, citySearchTerm]);

  // Handle city selection with null safety
  const handleCitySelect = (city) => {
    try {
      if (typeof setFilterCity === 'function') {
        setFilterCity(city || 'all');
      }
      setCityDropdownOpen(false);
      
      if (city === 'all' || !city) {
        setCitySearchTerm('');
      } else {
        setCitySearchTerm(String(city));
      }
    } catch (error) {
      console.error('Error in handleCitySelect:', error);
      setCityDropdownOpen(false);
    }
  };

  // Clear city filter with null safety
  const handleClearCity = (e) => {
    try {
      if (e && e.stopPropagation) {
        e.stopPropagation();
      }
      if (typeof setFilterCity === 'function') {
        setFilterCity('all');
      }
      setCitySearchTerm('');
    } catch (error) {
      console.error('Error in handleClearCity:', error);
    }
  };

  // Handle search term change with null safety
  const handleSearchTermChange = (e) => {
    try {
      const value = e?.target?.value || '';
      if (typeof setSearchTerm === 'function') {
        setSearchTerm(value);
      }
    } catch (error) {
      console.error('Error in handleSearchTermChange:', error);
    }
  };

  // Handle city search term change with null safety
  const handleCitySearchChange = (e) => {
    try {
      const value = e?.target?.value || '';
      setCitySearchTerm(value);
      setCityDropdownOpen(true);
    } catch (error) {
      console.error('Error in handleCitySearchChange:', error);
    }
  };

  // Handle view mode change with null safety
  const handleViewModeChange = (mode) => {
    try {
      if (typeof setViewMode === 'function' && (mode === 'grid' || mode === 'list')) {
        setViewMode(mode);
      }
    } catch (error) {
      console.error('Error in handleViewModeChange:', error);
    }
  };

  // Handle add company navigation with null safety
  const handleAddCompany = () => {
    try {
      if (typeof navigateToAddCompany === 'function') {
        navigateToAddCompany();
      }
    } catch (error) {
      console.error('Error in handleAddCompany:', error);
    }
  };

  // Update search term when filterCity changes externally
  useEffect(() => {
    try {
      if (filterCity === 'all' || !filterCity) {
        setCitySearchTerm('');
      } else {
        setCitySearchTerm(String(filterCity));
      }
    } catch (error) {
      console.error('Error in filterCity useEffect:', error);
    }
  }, [filterCity]);

  // Handle keyboard events with null safety
  const handleKeyDown = (e) => {
    try {
      if (e?.key === 'Escape') {
        setCityDropdownOpen(false);
      }
    } catch (error) {
      console.error('Error in handleKeyDown:', error);
    }
  };

  // Toggle dropdown with null safety
  const toggleCityDropdown = () => {
    try {
      setCityDropdownOpen(!cityDropdownOpen);
    } catch (error) {
      console.error('Error in toggleCityDropdown:', error);
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="mb-4">
          <div className="card-body">
            <div className="d-flex flex-column flex-lg-row gap-3 align-items-lg-center justify-content-between">
              <div className="d-flex flex-column flex-md-row gap-2 flex-grow-1">
                {/* Search with loading indicator */}
                <div className="position-relative">
                  <Search size={16} className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
                  <input
                    type="text"
                    className="form-control ps-5"
                    placeholder="Search companies, cities"
                    value={searchTerm || ''}
                    onChange={handleSearchTermChange}
                  />
                  {isLoading && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Searchable City Filter Dropdown */}
                <div className="position-relative" ref={cityDropdownRef}>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="All Cities"
                      value={citySearchTerm || ''}
                      onChange={handleCitySearchChange}
                      onFocus={() => setCityDropdownOpen(true)}
                      onKeyDown={handleKeyDown}
                    />
                    <div className="position-absolute top-50 end-0 translate-middle-y me-2 d-flex align-items-center gap-1">
                      {filterCity !== 'all' && filterCity && (
                        <button
                          type="button"
                          className="btn btn-sm p-0 border-0 bg-transparent"
                          onClick={handleClearCity}
                          title="Clear city filter"
                        >
                          <X size={14} className="text-muted" />
                        </button>
                      )}
                      <ChevronDown 
                        size={16} 
                        className={`text-muted transition-transform ${cityDropdownOpen ? 'rotate-180' : ''}`}
                        style={{ transition: 'transform 0.2s', cursor: 'pointer' }}
                        onClick={toggleCityDropdown}
                      />
                    </div>
                  </div>

                  {cityDropdownOpen && (
                    <div className="dropdown-menu show w-100 mt-1 shadow-lg border-0" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {/* All Cities option */}
                      <button
                        type="button"
                        className={`dropdown-item ${(filterCity === 'all' || !filterCity) ? 'active' : ''}`}
                        onClick={() => handleCitySelect('all')}
                      >
                        <div className="d-flex align-items-center">
                          <span>All Cities</span>
                          {(filterCity === 'all' || !filterCity) && (
                            <span className="ms-auto">
                              <i className="fas fa-check text-primary"></i>
                            </span>
                          )}
                        </div>
                      </button>

                      {/* Filtered cities */}
                      {filteredCities.length > 0 ? (
                        filteredCities.map((city, index) => (
                          <button
                            key={city || `city-${index}`}
                            type="button"
                            className={`dropdown-item ${filterCity === city ? 'active' : ''}`}
                            onClick={() => handleCitySelect(city)}
                          >
                            <div className="d-flex align-items-center">
                              <span>{city || 'Unknown City'}</span>
                              {filterCity === city && (
                                <span className="ms-auto">
                                  <i className="fas fa-check text-primary"></i>
                                </span>
                              )}
                            </div>
                          </button>
                        ))
                      ) : citySearchTerm ? (
                        <div className="dropdown-item-text text-muted">
                          No cities found matching "{citySearchTerm}"
                        </div>
                      ) : (
                        <div className="dropdown-item-text text-muted">
                          No cities available
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Optional Industry Filter */}
                {/*
                <select
                  className="form-select"
                  value={filterIndustry || 'all'}
                  onChange={(e) => {
                    try {
                      if (typeof setFilterIndustry === 'function') {
                        setFilterIndustry(e?.target?.value || 'all');
                      }
                    } catch (error) {
                      console.error('Error in industry filter change:', error);
                    }
                  }}
                  style={{ minWidth: '180px' }}
                >
                  <option value="all">All Industries</option>
                  {Array.isArray(industries) && industries.map((industry, index) => (
                    <option key={industry || `industry-${index}`} value={industry || ''}>
                      {industry || 'Unknown Industry'}
                    </option>
                  ))}
                </select>
                */}
              </div>

            <div className="d-flex justify-content-between align-items-center">
              {(searchTerm || filterIndustry !== 'all' || filterCity !== 'all') && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>

              {/* View toggle & Add button */}
              <div className="d-flex gap-2 justify-content-end">
                {!isMobile && 
                <div className="btn-group" role="group">
                  <button
                    className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleViewModeChange('grid')}
                    title="Grid View"
                  >
                    <Grid size={16} />
                  </button>
                  <button
                    className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleViewModeChange('list')}
                    title="List View"
                  >
                    <List size={16} />
                  </button>
                </div>}
                <button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={handleAddCompany}
                >
                  <Plus size={20} className="me-2" />
                  <span className="d-sm-inline">Add Company</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySearchFilter;